import {CompositionService} from "./services/CompositionService";
import {CompositionEventsEnum} from "./CompositionEvents";
import {CompositionStateStore} from "./store/CompositionStateStore";
import {ApplicationStore} from "./store/ApplicationStore";
import {updateAdjacency} from "./modules/timeline/utils/updateLayerClipsAdjacency";
import { ScriptLayerStore } from "./store/ScriptLayerStore";
import { Composition } from "../engine/composition";
import { LocalAssetStore } from "../storage/LocalAssetStore";
import { ParseTranscriptWords } from "../utils/Transcript";
import type { V2TranscriptWord } from "../pages/V2Editor/V2TextEditor";
import { CompositionPersistenceService } from "./services/CompositionPersistenceService";
import { ProjectAssetsStore } from "../state/ProjectAssetsStore";
import { LayoutService } from "./services/LayoutService";

class CompositionBridge {

    private initialized = false;
    private isRestoring = false; // Flag to skip persistence during restoration.
    private fetchTranscript?: (args: { trackId: string; projectId: string }) => Promise<any | undefined>;
    private scriptClipAudioSegmentsHashByClipId = new Map<string, string>();
    private transcriptHydrationInFlight = new Map<string, Promise<void>>();
    private transcriptHydrationRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private transcriptHydrationRetryCounts = new Map<string, number>();
    private static readonly TRANSCRIPT_RETRY_DELAY_MS = 2500;
    private static readonly TRANSCRIPT_MAX_RETRY_ATTEMPTS = 60;
    private captionsHydrationInFlight = new Map<string, Promise<void>>();
    private captionsHydrationRetryTimers = new Map<string, ReturnType<typeof setTimeout>>();
    private captionsHydrationRetryCounts = new Map<string, number>();
    private static readonly CAPTIONS_RETRY_DELAY_MS = 2500;
    private static readonly CAPTIONS_MAX_RETRY_ATTEMPTS = 60;

    init() {
        if (this.initialized) {
            return;
        }
        this.setupEventListeners();
    }

    /**
     * Set the restoring flag. When true, persistence writes are skipped.
     * Use this during composition restore to avoid re-persisting loaded data.
     */
    setRestoring(restoring: boolean) {
        this.isRestoring = restoring;
    }

    /**
     * Check if currently restoring (persistence writes disabled).
     */
    isRestoringComposition(): boolean {
        return this.isRestoring;
    }
    
    /**
     * Recalculate composition duration and update the store.
     * Call this after any clip add/update/remove operation.
     */
    private refreshDuration() {
        const composition = CompositionService.composition;
        // First, recalculate the composition's internal duration from its clips
        composition.recalculateDuration();
        // Then update the store from the composition
        CompositionStateStore.updateTimelineDuration();
    }

    /**
     * Allow UI to inject a transcript fetcher (can use auth-aware AssetStore).
     */
    setTranscriptFetcher(fetcher: (args: { trackId: string; projectId: string }) => Promise<any | undefined>) {
        this.fetchTranscript = fetcher;
    }

    /**
     * Manually sync the ScriptLayerStore with the current composition state.
     * Call this after composition restoration to ensure transcript words are loaded.
     */
    async syncScriptLayerStore() {
        const scriptClip = CompositionService.getScriptLayerClip?.();
        if (!scriptClip) {
            return;
        }

        await this.hydrateTranscriptWordsForClip(scriptClip.id, { allowRetry: true });

        // Also hydrate captions clips (words are derived from transcripts; we don't persist them).
        try {
            await this.hydrateCaptionsClips({ allowRetry: true });
        } catch {}
    }

    private async hydrateCaptionsClips(opts?: { allowRetry?: boolean }): Promise<void> {
        if (!CompositionService.isInitialized()) return;
        const comp: any = CompositionService.composition;
        if (!comp) return;

        const allowRetry = Boolean(opts?.allowRetry);
        const projectId = String((window as any).__librettoProjectId ?? "");
        if (!projectId) return;

        const captionsClips: any[] = [];
        for (const t of (comp.tracks ?? []) as any[]) {
            for (const c of (t?.clips ?? []) as any[]) {
                if (c && String(c.type) === "captions") captionsClips.push(c);
            }
        }
        if (!captionsClips.length) return;

        for (const c of captionsClips) {
            const hasWords = Array.isArray((c as any).words) && ((c as any).words?.length ?? 0) > 0;
            if (hasWords) continue;
            try {
                await this.hydrateWordsForCaptionsClip(c, { allowRetry, projectId });
            } catch {}
        }
    }

    private mapAbsCompTimeToScriptTime(scriptClip: any, absCompTime: number): number | null {
        const segments: any[] = Array.isArray(scriptClip?.scriptSegments) ? scriptClip.scriptSegments : [];
        if (!segments.length) {
            if (typeof scriptClip?.CompositionTimeToClipTime === "function") {
                return scriptClip.CompositionTimeToClipTime(absCompTime);
            }
            const startSec = Number(scriptClip?.start?.seconds ?? 0);
            return absCompTime - startSec;
        }
        for (const seg of segments) {
            const segDur = Number(seg?.srcEndSec ?? 0) - Number(seg?.srcStartSec ?? 0);
            const segEndComp = Number(seg?.compStartSec ?? 0) + segDur;
            if (absCompTime >= Number(seg?.compStartSec ?? 0) && absCompTime < segEndComp) {
                if (seg?.isGap) return null;
                const offsetIntoSeg = absCompTime - Number(seg?.compStartSec ?? 0);
                return Number(seg?.srcStartSec ?? 0) + offsetIntoSeg;
            }
        }
        return null;
    }

    private applyTranscriptEditsToWords(words: any[], scriptClip: any): any[] {
        const input = Array.isArray(words) ? words : [];
        if (!input.length) return input;

        const hidden: any[] = Array.isArray(scriptClip?.transcriptHiddenRanges) ? scriptClip.transcriptHiddenRanges : [];
        const corrections: any[] = Array.isArray(scriptClip?.wordCorrections) ? scriptClip.wordCorrections : [];
        if (!hidden.length && !corrections.length) return input;

        const correctionByKey = new Map<string, string>();
        if (corrections.length) {
            for (const c of corrections) {
                if (c == null) continue;
                const text = String(c.correctedText ?? "").trim();
                if (!text) continue;
                const sourceIndex = Number(c?.sourceIndex);
                const srcStartSec = Number(c?.srcStartSec);
                const srcEndSec = Number(c?.srcEndSec);
                if (!Number.isFinite(sourceIndex) || !Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec)) continue;
                correctionByKey.set(`${sourceIndex}:${srcStartSec}:${srcEndSec}`, text);
            }
        }

        const isHiddenAt = (t: number): boolean => {
            for (const r of hidden) {
                const s = Number(r?.startSec ?? 0);
                const e = Number(r?.endSec ?? s);
                if (!Number.isFinite(s) || !Number.isFinite(e) || e <= s) continue;
                if (t >= s && t < e) return true;
            }
            return false;
        };

        let changed = false;
        const out: any[] = [];
        for (const w of input) {
            const t = Number(w?.startSec ?? 0);
            if (hidden.length && Number.isFinite(t) && isHiddenAt(t)) {
                changed = true;
                continue;
            }

            let corrected: string | undefined;
            if (correctionByKey.size) {
                const sourceIndex = Number(w?.sourceIndex);
                const srcStartSec = Number(w?.srcStartSec);
                const srcEndSec = Number(w?.srcEndSec);
                if (Number.isFinite(sourceIndex) && Number.isFinite(srcStartSec) && Number.isFinite(srcEndSec)) {
                    corrected = correctionByKey.get(`${sourceIndex}:${srcStartSec}:${srcEndSec}`);
                }
            }
            if (corrected && corrected !== String(w?.text ?? "")) {
                changed = true;
                out.push({ ...w, text: corrected });
            } else {
                out.push(w);
            }
        }

        return changed ? out : input;
    }

    private refreshCaptionsFromTranscriptEdits(scriptClipId: string): void {
        if (!scriptClipId) return;
        if (!CompositionService.isInitialized()) return;
        const comp: any = CompositionService.composition;
        if (!comp) return;

        const scriptClip: any = CompositionService.getClipById(scriptClipId);
        if (!scriptClip || String(scriptClip.type) !== "script") return;

        let didUpdate = false;
        for (const t of (comp.tracks ?? []) as any[]) {
            for (const c of (t?.clips ?? []) as any[]) {
                if (!c || String(c.type) !== "captions") continue;
                const bound = String(c?.scriptClipId ?? "");
                if (bound && bound !== String(scriptClipId)) continue;

                const currentWords = Array.isArray((c as any).words) ? ((c as any).words as any[]) : [];
                if (!currentWords.length) continue;

                const rawWords =
                    Array.isArray((c as any).__rawWords) && ((c as any).__rawWords as any[]).length
                        ? ((c as any).__rawWords as any[])
                        : currentWords;
                if (!Array.isArray((c as any).__rawWords)) {
                    try { (c as any).__rawWords = rawWords; } catch {}
                }

                const nextWords = this.applyTranscriptEditsToWords(rawWords, scriptClip);
                if (nextWords !== currentWords) {
                    try { (c as any).words = nextWords; } catch {}
                    try { CompositionService.notifyClipUpdated(c); } catch {}
                    didUpdate = true;
                }
            }
        }

        if (didUpdate) {
            try { CompositionService.recomputeFrame?.(); } catch {}
            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        }
    }

    private getScriptClipAudioSegmentsHash(scriptClip: any): string {
        const entries: any[] = Array.isArray(scriptClip?.audioEntries) ? scriptClip.audioEntries : [];
        if (!entries.length) return "";

        const norm = (v: any, fallback = 0): number => {
            const n = Number(v);
            return Number.isFinite(n) ? n : fallback;
        };

        const parts: string[] = [];
        for (let i = 0; i < entries.length; i++) {
            const entry = entries[i];
            const segs: any[] = Array.isArray(entry?.segments) ? entry.segments : [];
            // Include the entry index + segment count so removals/reorders are detected.
            parts.push(`${i}:${segs.length}`);
            for (const seg of segs) {
                const offsetSec = norm(seg?.offsetSec);
                const inSec = norm(seg?.range?.inSec);
                const outSec = norm(seg?.range?.outSec);
                const rate = norm(seg?.playbackRate ?? entry?.playbackRate, 1) || 1;
                // Ignore segment IDs; we only care about timing/mapping.
                parts.push(`${offsetSec.toFixed(6)}|${inSec.toFixed(6)}|${outSec.toFixed(6)}|${rate.toFixed(6)}`);
            }
        }
        return parts.join(",");
    }

    private remapCaptionWordsToCurrentAudioSegments(words: any[], scriptClip: any): any[] {
        const input = Array.isArray(words) ? words : [];
        if (!input.length) return input;

        const entries: any[] = Array.isArray(scriptClip?.audioEntries) ? scriptClip.audioEntries : [];
        if (!entries.length) return input;

        const EPS = 1e-3;
        const out: any[] = [];
        let changed = false;

        const findSeg = (segs: any[], t: number): any | null => {
            if (!Number.isFinite(t)) return null;
            for (const seg of segs) {
                const inSec = Number(seg?.range?.inSec ?? NaN);
                const outSec = Number(seg?.range?.outSec ?? NaN);
                if (!Number.isFinite(inSec) || !Number.isFinite(outSec)) continue;
                if (t >= inSec - EPS && t < outSec + EPS) return seg;
            }
            return null;
        };

        for (const w of input) {
            const sourceIndex = Number((w as any)?.sourceIndex);
            const srcStartSec = Number((w as any)?.srcStartSec);
            const srcEndSec = Number((w as any)?.srcEndSec);
            if (!Number.isFinite(sourceIndex) || !Number.isFinite(srcStartSec) || !Number.isFinite(srcEndSec)) {
                out.push(w);
                continue;
            }

            const entry = entries[sourceIndex];
            const segs: any[] = Array.isArray(entry?.segments) ? entry.segments : [];
            if (!segs.length) {
                out.push(w);
                continue;
            }

            const startSeg = findSeg(segs, srcStartSec);
            if (!startSeg) {
                out.push(w);
                continue;
            }

            const endProbe = srcEndSec - EPS;
            const endSeg = findSeg(segs, endProbe) ?? startSeg;

            const startRate = Number(startSeg?.playbackRate ?? entry?.playbackRate ?? 1) || 1;
            const startOffset = Number(startSeg?.offsetSec ?? 0);
            const startIn = Number(startSeg?.range?.inSec ?? 0);
            const nextStart = (srcStartSec - startIn) / startRate + startOffset;

            const endRate = Number(endSeg?.playbackRate ?? entry?.playbackRate ?? 1) || 1;
            const endOffset = Number(endSeg?.offsetSec ?? 0);
            const endIn = Number(endSeg?.range?.inSec ?? 0);
            const nextEnd = (srcEndSec - endIn) / endRate + endOffset;

            if (!Number.isFinite(nextStart) || !Number.isFinite(nextEnd)) {
                out.push(w);
                continue;
            }

            const prevStart = Number((w as any)?.startSec ?? NaN);
            const prevEnd = Number((w as any)?.endSec ?? NaN);
            if (
                !Number.isFinite(prevStart) ||
                !Number.isFinite(prevEnd) ||
                Math.abs(prevStart - nextStart) > EPS ||
                Math.abs(prevEnd - nextEnd) > EPS
            ) {
                changed = true;
            }

            out.push({ ...(w as any), startSec: nextStart, endSec: nextEnd });
        }

        if (!changed) return input;

        out.sort((a, b) => Number(a?.startSec ?? 0) - Number(b?.startSec ?? 0));
        return out;
    }

    private refreshCaptionsFromScriptSourceSegments(scriptClipId: string): void {
        if (!scriptClipId) return;
        if (!CompositionService.isInitialized()) return;
        const comp: any = CompositionService.composition;
        if (!comp) return;

        const scriptClip: any = CompositionService.getClipById(scriptClipId);
        if (!scriptClip || String(scriptClip.type) !== "script") return;

        let didUpdate = false;
        for (const t of (comp.tracks ?? []) as any[]) {
            for (const c of (t?.clips ?? []) as any[]) {
                if (!c || String(c.type) !== "captions") continue;
                const bound = String(c?.scriptClipId ?? "");
                if (bound && bound !== String(scriptClipId)) continue;

                const currentWords = Array.isArray((c as any).words) ? ((c as any).words as any[]) : [];
                const rawWords =
                    Array.isArray((c as any).__rawWords) && ((c as any).__rawWords as any[]).length
                        ? ((c as any).__rawWords as any[])
                        : currentWords;
                if (!rawWords.length) continue;

                const remappedRaw = this.remapCaptionWordsToCurrentAudioSegments(rawWords, scriptClip);
                if (remappedRaw !== rawWords) {
                    try { (c as any).__rawWords = remappedRaw; } catch {}
                }
                const nextWords = this.applyTranscriptEditsToWords(remappedRaw, scriptClip);
                if (nextWords !== currentWords) {
                    try { (c as any).words = nextWords; } catch {}
                    try { CompositionService.notifyClipUpdated(c); } catch {}
                    didUpdate = true;
                }
            }
        }

        if (didUpdate) {
            try { CompositionService.recomputeFrame?.(); } catch {}
            try { window.dispatchEvent(new CustomEvent("updateHandlers")); } catch {}
        }
    }

	    private async hydrateWordsForCaptionsClip(
	        captionsClip: any,
	        opts: { allowRetry: boolean; projectId: string }
	    ): Promise<void> {
	        const captionsClipId = String(captionsClip?.id ?? "");
	        const existingInFlight = captionsClipId ? this.captionsHydrationInFlight.get(captionsClipId) : undefined;
	        if (existingInFlight) {
	            await existingInFlight;
	            return;
	        }

	        const run = (async () => {
	            const { allowRetry, projectId } = opts;
	            const session = CompositionService.getSessionVersion?.() ?? 0;

	            const scriptClipId = String(captionsClip?.scriptClipId ?? CompositionService.getScriptLayerClip?.()?.id ?? "");
	            const scriptClip: any = scriptClipId ? CompositionService.getClipById(scriptClipId) : null;
	            if (!scriptClip || String(scriptClip.type) !== "script") {
	                if (allowRetry) this.scheduleCaptionsHydrationRetry(captionsClipId, { projectId });
	                return;
	            }

	            const audioEntries: any[] = Array.isArray(scriptClip.getAudioEntries?.()) ? scriptClip.getAudioEntries() : [];
	            if (!audioEntries.length) {
	                if (allowRetry) this.scheduleCaptionsHydrationRetry(captionsClipId, { projectId });
	                return;
	            }

	            // If this captions clip is scene-scoped (start/stop not equal full script), filter words to its range.
	            let scriptRange: { startSec: number; endSec: number } | null = null;
	            try {
	                const startAbs = Number(captionsClip?.start?.seconds ?? 0);
	                const stopAbs = Number(captionsClip?.stop?.seconds ?? startAbs);
	                const s0 = this.mapAbsCompTimeToScriptTime(scriptClip, startAbs);
	                const s1 = this.mapAbsCompTimeToScriptTime(scriptClip, Math.max(startAbs, stopAbs - 1e-3));
	                if (s0 != null && s1 != null) {
	                    const a = Math.min(s0, s1);
	                    const b = Math.max(s0, s1);
	                    if (Number.isFinite(a) && Number.isFinite(b) && b > a) {
	                        scriptRange = { startSec: a, endSec: b };
	                    }
	                }
	            } catch {}

		            const rawWords: any[] = [];
		            const MIN_ENERGY_RATIO = 0.2;

		            for (let sourceIndex = 0; sourceIndex < audioEntries.length; sourceIndex++) {
		                const entry: any = audioEntries[sourceIndex];
		                const entryGain = Number(entry?.gain ?? 1);
		                if (Number.isFinite(entryGain) && entryGain <= 0.001) continue;
		                const trackId = entry?.assetId ? String(entry.assetId) : "";
		                if (!trackId) continue;

	                let transcriptJson: any | undefined;
	                try {
	                    if (this.fetchTranscript) {
	                        transcriptJson = await this.fetchTranscript({ trackId, projectId });
	                    }
	                } catch {
	                    transcriptJson = undefined;
	                }
	                if ((CompositionService.getSessionVersion?.() ?? 0) !== session) return;

	                if (!transcriptJson?.words || !Array.isArray(transcriptJson.words)) {
	                    if (allowRetry) this.scheduleCaptionsHydrationRetry(captionsClipId, { projectId });
	                    continue;
	                }

	                const entrySegments: Array<{ offsetSec: number; playbackRate: number; rangeInSec: number; rangeOutSec: number }> = [];
	                if (Array.isArray(entry?.segments) && entry.segments.length > 0) {
	                    for (const seg of entry.segments) {
	                        const offsetSec = Number(seg?.offsetSec ?? 0);
	                        const playbackRate = Number(seg?.playbackRate ?? entry?.playbackRate ?? 1.0) || 1.0;
	                        const rangeInSec = Number(seg?.range?.inSec ?? 0);
	                        const rangeOutSec = Number(seg?.range?.outSec ?? 0);
	                        if (!Number.isFinite(offsetSec) || !Number.isFinite(playbackRate) || playbackRate <= 0) continue;
	                        if (!Number.isFinite(rangeInSec) || !Number.isFinite(rangeOutSec) || rangeOutSec <= rangeInSec) continue;
	                        entrySegments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
	                    }
	                } else {
	                    const range = entry?.range;
	                    const offsetSec = Number(entry?.offsetSec ?? 0) || 0;
	                    const playbackRate = Number(entry?.playbackRate ?? 1.0) || 1.0;
	                    const sourceDuration = Number(ProjectAssetsStore.projectTracks?.find((t: any) => t.id === trackId || t.trackId === trackId)?.duration ?? 0);
	                    const rangeInSec = Number(range?.inSec ?? 0);
	                    const rangeOutSec = Number(range?.outSec ?? (sourceDuration > 0 ? sourceDuration : Infinity));
	                    entrySegments.push({ offsetSec, playbackRate, rangeInSec, rangeOutSec });
	                }

	                for (const seg of entrySegments) {
	                    for (const w of transcriptJson.words) {
	                        if ((w.type ?? "word") !== "word") continue;
	                        if (w.energy?.ratio != null && w.energy.ratio > 0 && w.energy.ratio < MIN_ENERGY_RATIO) continue;

	                        const wordStartSrc = Number(w.t0 ?? w.start ?? 0);
	                        const wordEndSrc = Number(w.t1 ?? w.end ?? wordStartSrc);
	                        if (!Number.isFinite(wordStartSrc) || !Number.isFinite(wordEndSrc)) continue;
	                        if (wordStartSrc < seg.rangeInSec || wordEndSrc > seg.rangeOutSec) continue;

	                        const startSec = (wordStartSrc - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
	                        const endSec = (wordEndSrc - seg.rangeInSec) / seg.playbackRate + seg.offsetSec;
	                        if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) continue;

	                        if (scriptRange) {
	                            if (startSec < scriptRange.startSec || endSec > scriptRange.endSec) continue;
	                        }

	                            const wordText = String(w.text ?? "").trim();

		                        rawWords.push({
		                            text: wordText,
		                            startSec,
		                            endSec,
		                            srcStartSec: wordStartSrc,
		                            srcEndSec: wordEndSrc,
		                            sourceIndex,
		                        });
		                    }
		                }
		            }

		            if ((CompositionService.getSessionVersion?.() ?? 0) !== session) return;

		            rawWords.sort((a, b) => (a.startSec ?? 0) - (b.startSec ?? 0));

		            try {
		                (captionsClip as any).__rawWords = rawWords;
		            } catch {}
		            try {
		                (captionsClip as any).words = this.applyTranscriptEditsToWords(rawWords, scriptClip);
		            } catch {}
		            if (captionsClipId) this.captionsHydrationRetryCounts.delete(captionsClipId);

	            try {
	                // Re-render once words are available.
	                CompositionService.recomputeFrame?.();
	            } catch {}
	            try {
	                window.dispatchEvent(new CustomEvent("updateHandlers"));
	            } catch {}
	        })();

	        if (captionsClipId) this.captionsHydrationInFlight.set(captionsClipId, run);
	        try {
	            await run;
	        } finally {
	            if (captionsClipId) this.captionsHydrationInFlight.delete(captionsClipId);
	        }
	    }

    private scheduleCaptionsHydrationRetry(captionsClipId: string, args: { projectId: string }): void {
        if (!captionsClipId) return;
        if (this.captionsHydrationRetryTimers.has(captionsClipId)) return;

        const currentAttempts = this.captionsHydrationRetryCounts.get(captionsClipId) ?? 0;
        if (currentAttempts >= CompositionBridge.CAPTIONS_MAX_RETRY_ATTEMPTS) return;
        this.captionsHydrationRetryCounts.set(captionsClipId, currentAttempts + 1);

        const scheduledSession = CompositionService.getSessionVersion?.() ?? 0;
        const timer = setTimeout(() => {
            this.captionsHydrationRetryTimers.delete(captionsClipId);
            if (!CompositionService.isInitialized()) return;
            if ((CompositionService.getSessionVersion?.() ?? 0) !== scheduledSession) return;

            const clip: any = CompositionService.getClipById(captionsClipId);
            if (!clip || String(clip.type) !== "captions") return;

            const hasWords = Array.isArray((clip as any).words) && ((clip as any).words?.length ?? 0) > 0;
            if (hasWords) {
                this.captionsHydrationRetryCounts.delete(captionsClipId);
                return;
            }

            void this.hydrateWordsForCaptionsClip(clip, { allowRetry: true, projectId: args.projectId });
        }, CompositionBridge.CAPTIONS_RETRY_DELAY_MS);

        this.captionsHydrationRetryTimers.set(captionsClipId, timer);
    }

    private scheduleTranscriptHydrationRetry(clipId: string): void {
        if (this.transcriptHydrationRetryTimers.has(clipId)) return;

        const currentAttempts = this.transcriptHydrationRetryCounts.get(clipId) ?? 0;
        if (currentAttempts >= CompositionBridge.TRANSCRIPT_MAX_RETRY_ATTEMPTS) return;
        this.transcriptHydrationRetryCounts.set(clipId, currentAttempts + 1);

        const timer = setTimeout(() => {
            this.transcriptHydrationRetryTimers.delete(clipId);
            // Best-effort: if composition is no longer initialized, bail.
            if (!CompositionService.isInitialized()) return;
            void this.hydrateTranscriptWordsForClip(clipId, { allowRetry: true });
        }, CompositionBridge.TRANSCRIPT_RETRY_DELAY_MS);

        this.transcriptHydrationRetryTimers.set(clipId, timer);
    }

    private async hydrateTranscriptWordsForClip(
        clipId: string,
        opts?: { allowRetry?: boolean }
    ): Promise<void> {
        const allowRetry = Boolean(opts?.allowRetry);
        const session = CompositionService.getSessionVersion?.() ?? 0;

        // Avoid duplicate concurrent hydrations for the same clipId.
        const existingInFlight = this.transcriptHydrationInFlight.get(clipId);
        if (existingInFlight) {
            await existingInFlight;
            return;
        }

        const run = (async () => {
            // Skip if already loaded with words.
            const existing = ScriptLayerStore.items.find(it => it.clipId === clipId);
            if (existing?.words?.length) {
                this.transcriptHydrationRetryCounts.delete(clipId);
                return;
            }

            // Ensure a placeholder item exists so UI can show transcribing state.
            ScriptLayerStore.setClipWords(clipId, existing?.words ?? []);

            const trackId = CompositionStateStore.clipIdToAssetId[clipId];
            if (!trackId) {
                if (allowRetry) this.scheduleTranscriptHydrationRetry(clipId);
                return;
            }

            try {
                let json: any | undefined;

                if (this.fetchTranscript) {
                    const projectId = String((window as any).__librettoProjectId ?? "");
                    if (projectId) {
                        json = await this.fetchTranscript({ trackId, projectId });
                    }
                }

                if ((CompositionService.getSessionVersion?.() ?? 0) !== session) return;

                if (!json) {
                    // Fallback to LocalAssetStore - lookup track to get activeTranscriptId
                    const track = ProjectAssetsStore.projectTracks?.find(
                        (t: any) => t.id === trackId || t.trackId === trackId
                    );
                    const transcriptId = track?.activeTranscriptId;
                    if (transcriptId) {
                        const buf = await LocalAssetStore.GetTranscript({ transcriptId });
                        if (buf) {
                            const text = new TextDecoder("utf-8").decode(buf);
                            json = JSON.parse(text);
                        }
                    }
                }

                if ((CompositionService.getSessionVersion?.() ?? 0) !== session) return;

                if (json) {
                    const parsed = ParseTranscriptWords(json);
                    const words: V2TranscriptWord[] = parsed.map(w => ({
                        text: w.word,
                        startSec: w.start,
                        endSec: w.end,
                        speaker: w.speaker,
                    }));

                    ScriptLayerStore.setClipWords(clipId, words);
                    this.transcriptHydrationRetryCounts.delete(clipId);
                    return;
                }
            } catch {}

            if (allowRetry) this.scheduleTranscriptHydrationRetry(clipId);
        })();

        this.transcriptHydrationInFlight.set(clipId, run);
        try {
            await run;
        } finally {
            this.transcriptHydrationInFlight.delete(clipId);
        }
    }

    private setupEventListeners() {
        const composition = CompositionService.composition;
        composition.on(CompositionEventsEnum.LAYER_ADDED, this.handleLayerAdded);
        composition.on(CompositionEventsEnum.LAYER_REMOVED, this.handleLayerRemoved);
        composition.on(CompositionEventsEnum.CLIP_ADDED, this.handleClipAdded);
        composition.on(CompositionEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
        composition.on(CompositionEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
        composition.on(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated);
        composition.on(CompositionEventsEnum.CANVAS_RESIZED, this.handleCanvasResized);
        composition.on(CompositionEventsEnum.CANVAS_BACKGROUND_CHANGED, this.handleCanvasBackgroundChanged)
        composition.on(CompositionEventsEnum.PLAYBACK_STATE, this.handlePlaying);
        composition.on(CompositionEventsEnum.PLAYBACK_TIME, this.handleTime);
        composition.on(CompositionEventsEnum.COMPOSITION_EMPTY, this.handleCompositionEmpty)
        this.initialized = true;
    }

    private handleLayerAdded = (event) => {
        const payload = event.detail;
        const layer = {id: payload.layer.id, clipIds: []}
        CompositionStateStore.addLayer(layer);
        
        // Persist layer to Firestore (skip during restoration)
        if (!this.isRestoring) {
            CompositionPersistenceService.addLayer(payload.layer.id);
            // Persist ordered layer ids
            CompositionPersistenceService.setOrderedLayerIds([...CompositionStateStore.orderedLayerIds]);
        }
    }

    public handleCompositionEmpty = (event) => {
        const payload = event.detail;
        const empty = Boolean(payload?.empty);
        CompositionStateStore.setIsCompositionEmpty(empty);

        // When a composition becomes empty, reset the playhead and timeline duration so that
        // the next inserted clip lands at t=0 and the UI can't seek past an empty timeline.
        if (empty) {
            try { CompositionService.pause(); } catch {}
            try { CompositionService.seek(0); } catch {}
            try { CompositionStateStore.setCurrentTime(0); } catch {}
            try { CompositionStateStore.setCompositionDuration(0); } catch {}
            try { CompositionStateStore.updateTimelineDuration(); } catch {}
        }
    }

    private handleLayerRemoved = (event) => {
        const payload = event.detail;
        CompositionStateStore.removeLayer(payload.layerId);
        
        // Persist layer removal to Firestore (skip during restoration)
        if (!this.isRestoring) {
            CompositionPersistenceService.deleteLayer(payload.layerId);
            // Persist updated ordered layer ids
            CompositionPersistenceService.setOrderedLayerIds([...CompositionStateStore.orderedLayerIds]);
        }
    }

    private handleClipRemoved = (event) => {
        const payload = event.detail;
        if (
            ApplicationStore.selectedClipId === payload.clipId ||
            ApplicationStore.selectedVisualElement?.clipId === payload.clipId
        ) {
            ApplicationStore.clearSelectedClipId();
        }

        // If the script layer clip was removed, ensure transcript/editor state is cleared.
        // This prevents stale transcript words from showing when no script clip exists.
        try {
            if (CompositionService.isScriptLayerClip?.(payload.clipId) || ScriptLayerStore.hasClip?.(payload.clipId)) {
                ScriptLayerStore.clear();
            }
        } catch {}

        CompositionStateStore.removeClip(payload.layerId, payload.clipId);
        updateAdjacency(payload.layerId);
        
        // Recalculate and update timeline duration after clip removal
        this.refreshDuration();
        
            // Persist clip removal to Firestore (skip during restoration)
            if (!this.isRestoring) {
                CompositionPersistenceService.deleteClip(payload.layerId, payload.clipId);
                CompositionPersistenceService.setOrderedClipIds(
                    payload.layerId,
                    [...(CompositionStateStore.layers?.[payload.layerId]?.clipIds ?? [])]
                );
            }
        }

	    private handleClipUpdated = (event) => {
	        const payload = event.detail;
	        const clip = payload.clip;
	        const isTransient = Boolean(payload?.transient);
	        try {
            // Build a store-friendly DTO
            const isMedia = 'range' in clip && 'offset' in clip;
            let startTime = 0;
            let duration = 0;
            let trimmedDuration = 0;

            if (isMedia) {
                const inSec = clip.range?.[0]?.seconds ?? 0;
                const outSec = clip.range?.[1]?.seconds ?? 0;
                const mediaDur = clip.duration?.seconds ?? Math.max(0, outSec - inSec);
                startTime = clip.start?.seconds ?? 0;
                trimmedDuration = clip.timelineDuration?.seconds ?? Math.max(0, outSec - inSec);
                duration = mediaDur;
            } else {
                const s = clip.start?.seconds ?? 0;
                const e = clip.stop?.seconds ?? s;
                startTime = s;
                duration = Math.max(0, e - s);
                trimmedDuration = clip.timelineDuration?.seconds ?? duration;
            }

	            CompositionStateStore.updateClip(clip.id, {
	                id: clip.id,
	                type: String(clip.type ?? 'unknown'),
	                startTime,
	                duration,
	                trimmedDuration,
	                hasPredecessor: false,
	                hasSuccessor: false,
	            });

	            // Transient updates are for interactive UX (e.g. Slip) and should not:
	            // - rebuild global duration
	            // - clamp/delete clips
	            // - persist to Firestore
	            // - generate undo steps
	            if (isTransient) {
	                return;
	            }

	            // If the first clip(s) added are non-script and their duration becomes known only after init,
	            // ensure we still create an empty ScriptClip spine so composition bounds are authoritative.
	            try {
	                if (!this.isRestoring) {
	                    const hasScript = Boolean(CompositionService.getScriptLayerClip?.());
	                    if (!hasScript && String(clip?.type) !== 'script') {
	                        let maxEndSec = Math.max(0, Number(startTime ?? 0) + Number(trimmedDuration ?? 0));
	                        const clips = (CompositionStateStore as any).clips ?? {};
	                        for (const c of Object.values(clips) as any[]) {
	                            if (!c || String((c as any).type) === 'script') continue;
	                            const end = Number((c as any).startTime ?? 0) + Number((c as any).trimmedDuration ?? 0);
	                            if (Number.isFinite(end) && end > maxEndSec) maxEndSec = end;
	                        }
	                        if (Number.isFinite(maxEndSec) && maxEndSec > 0) {
	                            void CompositionService.ensureEmptyScriptSpine(maxEndSec);
	                        }
	                    }
	                }
	            } catch {}

	            // Enforce ScriptClip-defined bounds: non-script clips must not extend past the script end.
	            // Do this on CLIP_UPDATED as well (some clips report duration/timelineDuration only after init).
	            try {
                const scriptEnd = CompositionService.getScriptEndSec();
                if (scriptEnd != null && Number.isFinite(scriptEnd)) {
                    const EPS = 1e-3;
                    const clipType = String(clip?.type ?? 'unknown');
                    if (clipType === 'script') {
                        CompositionService.clampAllNonScriptClipsToScriptEnd();
                    } else {
                        const start = Number(startTime ?? 0);
                        const end = start + Number(trimmedDuration ?? 0);
                        if (Number.isFinite(start) && Number.isFinite(end)) {
                            if (start >= scriptEnd - EPS) {
                                CompositionService.deleteClipById(String(clip.id));
                            } else if (end > scriptEnd + EPS) {
                                const delta = scriptEnd - end; // negative (shrink)
                                CompositionService.trimClipRight(String(clip.id), delta);
                            }
                        }
                    }
                }
            } catch {}

            // Recalculate and update timeline duration after clip update
            this.refreshDuration();
            
            // Persist clip update to Firestore (skip during restoration)
            if (!this.isRestoring) {
                CompositionPersistenceService.updateClip(clip);
            }
        } catch (e) {
            console.warn('handleClipUpdated: failed to map clip payload', e);
        }
    }

    private handleClipAdded = (event) => {
        const payload = event.detail;
        const clip = payload.clip;
        try {
            // Build a store-friendly DTO
            const isMedia = 'range' in clip && 'offset' in clip;
            let startTime = 0;
            let duration = 0;
            let trimmedDuration = 0;

            if (isMedia) {
                const inSec = clip.range?.[0]?.seconds ?? 0;
                const outSec = clip.range?.[1]?.seconds ?? 0;
                const mediaDur = clip.duration?.seconds ?? Math.max(0, outSec - inSec);
                startTime = clip.start?.seconds ?? 0;
                trimmedDuration = clip.timelineDuration?.seconds ?? Math.max(0, outSec - inSec);
                duration = mediaDur;
            } else {
                const s = clip.start?.seconds ?? 0;
                const e = clip.stop?.seconds ?? s;
                startTime = s;
                duration = Math.max(0, e - s);
                trimmedDuration = clip.timelineDuration?.seconds ?? duration;
            }

            CompositionStateStore.addClip(payload.layerId, {
                id: clip.id,
                type: String(clip.type ?? 'unknown'),
                startTime,
                duration,
                trimmedDuration,
                hasPredecessor: false,
                hasSuccessor: false,
            });

            // If this is the first inserted clip and it's non-script, create an empty ScriptClip spine
            // so the composition always has authoritative bounds (ScriptClip starts at t=0).
            try {
                const hasScript = Boolean(CompositionService.getScriptLayerClip?.());
                if (!hasScript && String(clip?.type) !== 'script') {
                    const endSec = Math.max(0, Number(startTime ?? 0) + Number(trimmedDuration ?? 0));
                    if (Number.isFinite(endSec) && endSec > 0) {
                        void CompositionService.ensureEmptyScriptSpine(endSec);
                    }
                }
            } catch {}

            // Enforce ScriptClip-defined bounds: non-script clips must not extend past the script end.
            // (Do this before recomputing duration so the timeline can't grow past the script clip.)
            try {
                CompositionService.clampAllNonScriptClipsToScriptEnd();
            } catch {}
            
            // Recalculate and update timeline duration after clip added
            this.refreshDuration();
            
            // Persist clip to Firestore (skip during restoration)
            if (!this.isRestoring) {
                CompositionPersistenceService.addClip(clip, payload.layerId);
                CompositionPersistenceService.setOrderedClipIds(
                    payload.layerId,
                    [...(CompositionStateStore.layers?.[payload.layerId]?.clipIds ?? [])]
                );
            }

            // Keep ScriptLayerStore speaker overrides in sync when a script clip is added/restored.
            try {
                if (String(clip?.type) === 'script') {
                    ScriptLayerStore.updateSpeakerOverrides(Array.isArray(clip?.speakerOverrides) ? clip.speakerOverrides : []);
                    ScriptLayerStore.updateSpeakerMuteRanges(
                        Array.isArray((clip as any)?.speakerMuteRanges) ? (clip as any).speakerMuteRanges : []
                    );
                }
            } catch {}

            // Captions clips hydrate their words asynchronously (derived from transcripts).
            // Trigger hydration on insertion so layout-managed captions appear immediately.
            try {
                if (String(clip?.type) === "captions") {
                    const projectId = String(ProjectAssetsStore.projectId ?? (window as any).__librettoProjectId ?? "");
                    const hasWords = Array.isArray((clip as any)?.words) && (((clip as any).words as any[])?.length ?? 0) > 0;
                    if (projectId && !hasWords) {
                        void this.hydrateWordsForCaptionsClip(clip, { allowRetry: true, projectId });
                    }
                }
            } catch {}
        } catch (e) {
            console.warn('handleClipAdded: failed to map clip payload', e);
        }
    }

    private handleCanvasResized = (event) => {
        CompositionStateStore.setDisplayResolution(event.detail.width, event.detail.height);

        // Recompute default script clip placement for the new canvas size/aspect,
        // and re-apply any explicit per-scene layout presets for the current aspect.
        try {
            const scriptClip: any = CompositionService.getScriptLayerClip?.();
            if (scriptClip) {
                try {
                    CompositionService.applyDefaultScriptClipPlacement(String(scriptClip.id));
                } catch {}

                try {
                    const scenes: any[] = typeof scriptClip.getScenes === "function" ? scriptClip.getScenes() : [];
                    for (let i = 0; i < (scenes?.length ?? 0); i++) {
                        const layout = scriptClip.getSceneLayout?.(i) as string | null | undefined;
                        if (layout) {
                            try { LayoutService.applySceneLayout(i, layout); } catch {}
                        }
                    }
                } catch {}
            }
        } catch {}
        
        // Persist canvas size to Firestore (skip during restoration)
        if (!this.isRestoring) {
            CompositionPersistenceService.updateDisplaySettings({
                width: event.detail.width,
                height: event.detail.height,
            });
        }
    }

    private handleCanvasBackgroundChanged = (event) => {
        const payload = event.detail;
        CompositionStateStore.setDisplayBackgroundColor(payload.color);
        
        // Persist background color to Firestore (skip during restoration)
        if (!this.isRestoring) {
            CompositionPersistenceService.updateDisplaySettings({
                backgroundColor: payload.color,
            });
        }
    }

    private handlePlaying = (event) => {
        const payload = event.detail;
        CompositionStateStore.setIsPlaying(payload.isPlaying);
    }

    private handleTime = (event) => {
        const payload = event.detail;
        const currentTime = payload.time;
        const fitDuration = CompositionService.getFitDuration();
        const stopAt = Math.max(0, fitDuration - 1 / CompositionService.getFps());

        if (currentTime >= stopAt && CompositionStateStore.isPlaying) {
            CompositionService.pause();
            CompositionService.seek(stopAt);
        }

        CompositionStateStore.setCurrentTime(currentTime);
    }

	    private handleScriptLayerUpdated = (event) => {
        const payload = event.detail;
        const script = payload?.script as {
            layerId: string | null;
            clipId?: string | null;
            clipIds?: string[];
            cutsChanged?: boolean;
            gapsChanged?: boolean;
            insertionsChanged?: boolean;
            splitsChanged?: boolean;
            speakerOverridesChanged?: boolean;
            speakerMutesChanged?: boolean;
            transcriptHiddenRangesChanged?: boolean;
            wordCorrectionsChanged?: boolean;
            transcriptParagraphBreaksChanged?: boolean;
            transcriptMarkersChanged?: boolean;
        } | undefined;
        if (!script) return;

        // Keep ScriptClip as the visual base layer by ensuring its track sits behind other tracks.
        // This makes non-script layer order operations behave intuitively (other layers can sit above the spine).
        try {
            CompositionService.ensureScriptLayerTrackAtBack({ persist: !this.isRestoring });
        } catch {}

        // Keep ScriptLayerStore speaker overrides in sync with the engine script clip.
        if (script.clipId) {
            try {
                const clip: any = CompositionService.getClipById(script.clipId);
                if (clip && String(clip.type) === 'script') {
                    ScriptLayerStore.updateSpeakerOverrides(Array.isArray(clip.speakerOverrides) ? clip.speakerOverrides : []);
                    ScriptLayerStore.updateSpeakerMuteRanges(
                        Array.isArray((clip as any).speakerMuteRanges) ? (clip as any).speakerMuteRanges : []
                    );
                    ScriptLayerStore.updateTranscriptHiddenRanges(
                        Array.isArray((clip as any).transcriptHiddenRanges) ? (clip as any).transcriptHiddenRanges : []
                    );
                    ScriptLayerStore.updateWordCorrections(
                        Array.isArray((clip as any).wordCorrections) ? (clip as any).wordCorrections : []
                    );
                    ScriptLayerStore.updateTranscriptParagraphBreaks(
                        Array.isArray((clip as any).transcriptParagraphBreaks) ? (clip as any).transcriptParagraphBreaks : []
                    );
                    ScriptLayerStore.updateTranscriptMarkers(
                        Array.isArray((clip as any).transcriptMarkers) ? (clip as any).transcriptMarkers : []
                    );
                }
            } catch {}
        }

        // Keep captions clips in sync when transcript-only edits change (word corrections / hidden ranges).
        if ((script.wordCorrectionsChanged || script.transcriptHiddenRangesChanged) && script.clipId) {
            try { this.refreshCaptionsFromTranscriptEdits(String(script.clipId)); } catch {}
        }

        // Keep captions clips in sync when the ScriptClip's per-source segment mapping changes.
        // This matters for operations like inserting empty scenes which shift source segments without changing transcript data.
        if (script.clipId) {
            try {
                const clip: any = CompositionService.getClipById(script.clipId);
                if (clip && String(clip.type) === "script") {
                    const nextHash = this.getScriptClipAudioSegmentsHash(clip);
                    const prevHash = this.scriptClipAudioSegmentsHashByClipId.get(String(script.clipId));
                    if (prevHash !== nextHash) {
                        this.scriptClipAudioSegmentsHashByClipId.set(String(script.clipId), nextHash);
                        try { this.refreshCaptionsFromScriptSourceSegments(String(script.clipId)); } catch {}
                    }
                }
            } catch {}
        }
        
        // If cuts changed, update the clip's duration in CompositionStateStore
        // (gaps don't affect duration since they maintain timeline position)
        if (script.cutsChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (clip && 'effectiveDuration' in clip) {
                const effectiveDur = clip.timelineDuration?.seconds ?? clip.effectiveDuration ?? clip.duration?.seconds ?? 0;
                const existingClip = CompositionStateStore.clips[script.clipId];
                if (existingClip) {
                    CompositionStateStore.updateClip(script.clipId, {
                        ...existingClip,
                        trimmedDuration: effectiveDur,
                    });
                }
                
                // Persist cuts/gaps/splits changes to Firestore (skip during restoration)
                if (!this.isRestoring && clip.track?.id) {
                    CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                        scriptCuts: clip.scriptCuts,
                        gaps: clip.gaps,
                        gapInsertions: clip.gapInsertions,
                        splits: clip.splits,
                    });
                }
            }
            
            // Recalculate and update timeline duration after cuts
            try { CompositionService.clampAllNonScriptClipsToScriptEnd(); } catch {}
            this.refreshDuration();
        }

        // Persist gap insertion changes (gap insertions DO affect duration).
	        if (script.insertionsChanged && script.clipId) {
	            const clip: any = CompositionService.getClipById(script.clipId);
	            if (clip) {
                const timelineDur = clip.timelineDuration?.seconds ?? clip.effectiveDuration ?? clip.duration?.seconds ?? 0;
                const existingClip = CompositionStateStore.clips[script.clipId];
                if (existingClip) {
                    CompositionStateStore.updateClip(script.clipId, {
                        ...existingClip,
                        trimmedDuration: timelineDur,
                    });
                }

                if (!this.isRestoring && clip.track?.id) {
                    CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                        gapInsertions: clip.gapInsertions,
                    });
                }
	            }

	            try { CompositionService.clampAllNonScriptClipsToScriptEnd(); } catch {}
	            this.refreshDuration();
	        }
        
        // Persist gaps changes (gaps don't affect duration but should still be persisted)
        if (script.gapsChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    gaps: clip.gaps,
                });
            }
        }

        // Persist split changes (scene boundaries).
        if (script.splitsChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    splits: clip.splits,
                });
            }
        }

        // Persist speaker override changes (manual speaker assignments).
        if (script.speakerOverridesChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    speakerOverrides: clip.speakerOverrides,
                });
            }
        }

        // Persist speaker mute range changes (audio-only speaker muting).
        // Also persist on timing edits because operations like empty-scene insertion ripple these ranges.
        if ((script.speakerMutesChanged || script.cutsChanged || script.gapsChanged || script.insertionsChanged || script.splitsChanged) && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    speakerMuteRanges: (clip as any).speakerMuteRanges ?? [],
                });
            }
        }

        // Persist transcript-hidden-range changes (transcript-only edits).
        if (script.transcriptHiddenRangesChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    transcriptHiddenRanges: (clip as any).transcriptHiddenRanges ?? [],
                });
            }
        }

        // Persist word correction changes (transcript-only edits).
        if (script.wordCorrectionsChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    wordCorrections: (clip as any).wordCorrections ?? [],
                });
            }
        }

        // Persist transcript paragraph break changes (transcript-only structure).
        if (script.transcriptParagraphBreaksChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    transcriptParagraphBreaks: (clip as any).transcriptParagraphBreaks ?? [],
                });
            }
        }

        // Persist transcript marker changes (transcript-only navigation).
        if (script.transcriptMarkersChanged && script.clipId) {
            const clip: any = CompositionService.getClipById(script.clipId);
            if (!this.isRestoring && clip?.track?.id) {
                CompositionPersistenceService.updateScriptClipEdits(script.clipId, clip.track.id, {
                    transcriptMarkers: (clip as any).transcriptMarkers ?? [],
                });
            }
        }

	        // Support both clipIds (array) and clipId (single) formats
	        let desired: string[] = [];
	        if (Array.isArray(script.clipIds)) {
	            desired = script.clipIds;
	        } else if (script.clipId) {
	            desired = [script.clipId];
	        }
	
	        // Transcript editor should only reflect the ScriptLayerClip (script clip), not other media clips.
	        // Some legacy callsites may emit extra clipIds; filter defensively.
	        try {
	            const scriptLayerClipId = (CompositionService.composition as any)?.scriptLayer?.clipId;
	            if (scriptLayerClipId) {
	                desired = [String(scriptLayerClipId)];
	            } else {
	                desired = desired.filter((id) => {
	                    const c: any = CompositionService.getClipById(id);
	                    return c && String(c.type) === "script";
	                });
	            }
	        } catch {}
	        const current = ScriptLayerStore.items.map((it) => it.clipId);

        // Remove clips no longer present
        for (const clipId of current) {
            if (!desired.includes(clipId)) {
                ScriptLayerStore.removeByClipId(clipId);
            }
	        }

	        // Add newly present clips (hydrate words from local cache if available)
	        // Map composition clipId -> trackId/assetId (for transcript lookup)
	        for (const clipId of desired) {
	            const existing = ScriptLayerStore.items.find((it) => it.clipId === clipId);
	            const hasWords = (existing?.words?.length ?? 0) > 0;
            if (!hasWords) {
                void this.hydrateTranscriptWordsForClip(clipId, { allowRetry: true });
            }
        }

        // Reorder to match engine
        ScriptLayerStore.reorderByClipIds(desired);
    }

    teardown() {
        if (!this.initialized) return;
        // Stop any pending transcript hydration retries.
        try {
            for (const timer of this.transcriptHydrationRetryTimers.values()) {
                clearTimeout(timer);
            }
        } catch {}
        this.transcriptHydrationRetryTimers.clear();
        this.transcriptHydrationInFlight.clear();
        this.transcriptHydrationRetryCounts.clear();
        this.scriptClipAudioSegmentsHashByClipId.clear();
        // Stop any pending captions hydration retries.
        try {
            for (const timer of this.captionsHydrationRetryTimers.values()) {
                clearTimeout(timer);
            }
        } catch {}
        this.captionsHydrationRetryTimers.clear();
        this.captionsHydrationInFlight.clear();
        this.captionsHydrationRetryCounts.clear();
        try {
            const composition = CompositionService.composition as any;
            composition.off?.(CompositionEventsEnum.LAYER_ADDED, this.handleLayerAdded);
            composition.off?.(CompositionEventsEnum.LAYER_REMOVED, this.handleLayerRemoved);
            composition.off?.(CompositionEventsEnum.CLIP_ADDED, this.handleClipAdded);
            composition.off?.(CompositionEventsEnum.CLIP_UPDATED, this.handleClipUpdated);
            composition.off?.(CompositionEventsEnum.CLIP_REMOVED, this.handleClipRemoved);
            composition.off?.(CompositionEventsEnum.SCRIPT_LAYER_UPDATED, this.handleScriptLayerUpdated);
            composition.off?.(CompositionEventsEnum.CANVAS_RESIZED, this.handleCanvasResized);
            composition.off?.(CompositionEventsEnum.CANVAS_BACKGROUND_CHANGED, this.handleCanvasBackgroundChanged);
            composition.off?.(CompositionEventsEnum.PLAYBACK_STATE, this.handlePlaying);
            composition.off?.(CompositionEventsEnum.PLAYBACK_TIME, this.handleTime);
            composition.off?.(CompositionEventsEnum.COMPOSITION_EMPTY, this.handleCompositionEmpty);
        } catch {}
        this.initialized = false;
        this.isRestoring = false;
        try { CompositionStateStore.reset(); } catch {}
        try { ScriptLayerStore.clear(); } catch {}
    }
}

export default new CompositionBridge();
